import { ArrowUp } from 'iconsax-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AppRoutes } from '@/constants/appRoutes'
import { cn } from '@/lib/utils'
import type { FaceCheckData } from '@/store/api/Workspace/Workspace.types'
import { formatDate } from '@/utils/date'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { FaceMatchAvatars } from './FaceMatchAvatars'

interface FaceMatchItemProps {
  data: FaceCheckData
  showActions?: boolean
}

export const FaceMatchItem = ({
  data,
  showActions = true,
}: FaceMatchItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const requesterName = data.requester.fullName
  const isRowClickable = showActions

  const handleRowClick = () =>
    navigate(AppRoutes.faceMatchResults, { state: data._id })

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleRowClick()
    }
  }

  return (
    <div
      role={isRowClickable ? 'button' : undefined}
      tabIndex={isRowClickable ? 0 : undefined}
      onClick={isRowClickable ? handleRowClick : undefined}
      onKeyDown={isRowClickable ? handleRowKeyDown : undefined}
      className={cn(
        'flex items-center w-full border-b border-neutralColor5 last:border-b-0 gap-2 min-h-[72px]',
        isRowClickable &&
          'cursor-pointer hover:bg-baseColor transition-colors focus-visible:outline-none focus-visible:bg-baseColor',
      )}
    >
      <div className="flex flex-1 min-w-0 items-center px-2">
        <FaceMatchAvatars items={data.output} />
      </div>

      <div className="hidden md:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor truncate">
          {t('dashboard.reportsScreen.faceMatch')}
        </p>
      </div>

      <div className="hidden lg:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor">
          {formatDate(data.createdAt, 'MM/dd/yyyy')}
        </p>
      </div>

      <div className="hidden lg:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor truncate">
          {t('dashboard.reportsScreen.by')} {requesterName}
        </p>
      </div>

      <div className="w-[135px] shrink-0 px-2" />

      {showActions && (
        <div className="w-10 flex justify-end mr-2 shrink-0">
          <span
            aria-hidden="true"
            className="p-1.5 rounded-md pointer-events-none"
          >
            <ArrowUp
              size={20}
              color={weCheckPalette.neutralColor}
              className="rotate-45"
            />
          </span>
        </div>
      )}
    </div>
  )
}
